// src/component/AdminPanelUserList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useAdminApi } from "./adminApi";

/* ---------- Утилиты UI ---------- */
const Spinner = ({ className = "h-4 w-4" }) => (
  <span
    className={`inline-block ${className} border-2 border-black/20 border-t-black rounded-full animate-spin`}
    aria-hidden="true"
  />
);

const initials = (first, last) => {
  const f = (first || "").trim()[0] || "";
  const l = (last || "").trim()[0] || "";
  return (f + l).toUpperCase();
};

const RoleBadge = ({ role }) => {
  const name = (role || "").toString().toLowerCase();
  const map = {
    admin: "bg-red-50 text-red-700 ring-red-200",
    manager: "bg-blue-50 text-blue-700 ring-blue-200",
    staff: "bg-amber-50 text-amber-700 ring-amber-200",
    user: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  };
  const cls = map[name] || "bg-gray-50 text-gray-700 ring-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ${cls}`}>
      {role || "—"}
    </span>
  );
};

export default function AdminPanelUserList() {
  const api = useAdminApi();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null); // id юзера, у которого сохраняем роль

  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  /* ---------- Загрузка ---------- */
  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data, status } = await api.get("admin/users/");
      if (status === 200 && Array.isArray(data)) setUsers(data);
      else setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await loadUsers();
      const { data, status } = await api.get("admin/roles/");
      if (status === 200 && Array.isArray(data)) setRoles(data);
      else setRoles([]);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- Действия ---------- */
  const handleRoleChange = async (userId, roleId) => {
    // оптимистичное обновление
    setSavingId(userId);
    const prev = users;
    setUsers((list) =>
      list.map((u) =>
        u.id === userId ? { ...u, role_id: Number(roleId), role: roles.find((r) => r.id === Number(roleId))?.name || u.role } : u
      )
    );
    try {
      const { status } = await api.post(`admin/users/${userId}/toggle-role/`, { role_id: roleId });
      if (status !== 200) {
        // откат
        setUsers(prev);
      }
    } catch {
      setUsers(prev);
    } finally {
      setSavingId(null);
    }
  };

  /* ---------- Фильтрация/поиск ---------- */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      const byRole = roleFilter ? String(u.role_id) === String(roleFilter) : true;
      if (!byRole) return false;
      if (!q) return true;
      const fio = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
      return (
        fio.includes(q) ||
        (u.login || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
      );
    });
  }, [users, query, roleFilter]);

  const roleOptions = useMemo(() => [{ id: "", name: "Все роли" }, ...roles], [roles]);

  /* ---------- Скелетоны ---------- */
  const SkeletonRow = () => (
    <tr>
      <td className="p-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-gray-200 animate-pulse" />
          <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
        </div>
      </td>
      <td className="p-3"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
      <td className="p-3"><div className="h-4 w-40 bg-gray-200 rounded animate-pulse" /></td>
      <td className="p-3"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse" /></td>
      <td className="p-3"><div className="h-5 w-16 bg-gray-200 rounded-full animate-pulse" /></td>
      <td className="p-3"><div className="h-9 w-32 bg-gray-200 rounded animate-pulse" /></td>
    </tr>
  );

  const SkeletonCard = () => (
    <div className="p-4 rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
        <div className="flex-1">
          <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
      <div className="mt-3 h-3 w-44 bg-gray-200 rounded animate-pulse" />
      <div className="mt-3 h-9 w-full bg-gray-200 rounded animate-pulse" />
    </div>
  );

  return (
    <section className="space-y-4">
      {/* Заголовок и панель управления */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold">Пользователи</h2>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск: ФИО, логин, email"
              className="w-64 max-w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">⌘K</span>
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          >
            {roleOptions.map((r) => (
              <option key={String(r.id)} value={String(r.id)}>{r.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={loadUsers}
            className="inline-flex items-center gap-2 rounded-md bg-white ring-1 ring-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            title="Обновить список"
          >
            {loading ? <Spinner /> : <span className="inline-block">↻</span>}
            Обновить
          </button>
        </div>
      </div>

      {/* Счётчик */}
      <div className="text-sm text-gray-500">
        Найдено: <span className="font-medium text-gray-700">{filtered.length}</span>
      </div>

      {/* Мобильный вид — карточки */}
      <div className="grid grid-cols-1 gap-3 sm:hidden">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : filtered.map((u) => (
              <div key={u.id} className="p-4 rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold">
                    {initials(u.first_name, u.last_name) || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-semibold truncate">{u.first_name} {u.last_name}</div>
                      <RoleBadge role={u.role} />
                    </div>
                    <div className="text-xs text-gray-500 truncate">{u.login} · {u.email}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Зарегистрирован:{" "}
                      {u.created_date ? new Date(u.created_date).toLocaleDateString("ru-RU") : "—"}
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-xs text-gray-600 mb-1">Роль</label>
                  <div className="flex items-center gap-2">
                    <select
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50"
                      value={u.role_id || ""}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      disabled={savingId === u.id}
                    >
                      {roles.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                    {savingId === u.id && <Spinner />}
                  </div>
                </div>
              </div>
            ))
        }

        {!loading && filtered.length === 0 && (
          <div className="p-6 text-center text-gray-500 ring-1 ring-gray-200 rounded-xl bg-white">
            Ничего не найдено. Попробуйте изменить фильтры.
          </div>
        )}
      </div>

      {/* Десктопный вид — таблица */}
      <div className="hidden sm:block overflow-x-auto rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Пользователь</th>
              <th className="p-3 font-semibold">Логин</th>
              <th className="p-3 font-semibold">E-mail</th>
              <th className="p-3 font-semibold">Дата регистрации</th>
              <th className="p-3 font-semibold">Роль</th>
              <th className="p-3 font-semibold">Изменить роль</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : filtered.map((u) => (
                  <tr key={u.id} className="border-t last:border-b">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold">
                          {initials(u.first_name, u.last_name) || "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{u.first_name} {u.last_name}</div>
                          <div className="text-xs text-gray-500 truncate">{u.login}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">{u.login}</td>
                    <td className="p-3">{u.email}</td>
                    <td className="p-3">
                      {u.created_date ? new Date(u.created_date).toLocaleDateString("ru-RU") : "—"}
                    </td>
                    <td className="p-3">
                      <RoleBadge role={u.role} />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <select
                          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50"
                          value={u.role_id || ""}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          disabled={savingId === u.id}
                        >
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                        {savingId === u.id && <Spinner />}
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>

        {!loading && filtered.length === 0 && (
          <div className="p-6 text-center text-gray-500">Ничего не найдено. Попробуйте изменить фильтры.</div>
        )}
      </div>
    </section>
  );
}
